import { GA4_DEBUG_MODE } from '@/constants'
import type {
	GA4Config, QueuedEvent
} from '@/types/analytics'
import {
	computed,
	readonly,
	ref
} from 'vue'

const defaultConfig: GA4Config = {
	maxQueueSize: 100,
	maxQueueAge: 30000, // 30 seconds
	initTimeout: 5000, // 5 seconds
	enableVisitorTracking: true,
	enableDebugLogging: GA4_DEBUG_MODE
}

// Event history for debug panel
const eventHistory = ref<Array<{
	name: string
	params: any
	timestamp: number
}>>([])

export const useGA4EventQueue = () => {
	// Global state using Nuxt's useState
	const queue = useState<QueuedEvent[]>('ga4-queue', () => [])
	const isInitialized = useState('ga4-initialized', () => false)
	const initPromise = useState<Promise<void> | null>('ga4-init-promise', () => null)
	const config = useState<GA4Config>('ga4-config', () => defaultConfig)

	// dev client only Logging helpers
	const log = {
		info: (...args: any[]) => {
			if (import.meta.client && config.value.enableDebugLogging) {
				console.log('[GA4]', ...args)
			}
		},
		warn: (...args: any[]) => {
			if (import.meta.client && config.value.enableDebugLogging) {
				console.warn('[GA4]', ...args)
			}
		},
		error: (...args: any[]) => {
			if (import.meta.client && config.value.enableDebugLogging) {
				console.error('[GA4]', ...args)
			}
		}
	}

	// Clean expired events from queue
	const cleanQueue = () => {
		const now = Date.now()
		const before = queue.value.length
		queue.value = queue.value.filter(event =>
			now - event.timestamp < config.value.maxQueueAge
		)
		const removed = before - queue.value.length
		if (removed > 0) {
			log.info(`Cleaned ${removed} expired events from queue`)
		}
	}

	// Sanitize parameters according to GA4 limits
	const sanitizeParameters = (parameters?: Record<string, any>): Record<string, any> | undefined => {
		if (!parameters) return undefined

		return Object.entries(parameters).reduce((acc, [ key, value ]) => {
			// Skip null/undefined values
			if (value === null || value === undefined) return acc

			// GA4 limits: parameter names max 40 chars
			const sanitizedKey = key.substring(0, 40).replace(/[^a-zA-Z0-9_]/g, '_')

			// Handle different value types
			let sanitizedValue = value
			if (typeof value === 'string') {
				// GA4 limits: string values max 100 chars for event parameters
				sanitizedValue = value.substring(0, 100)
			} else if (typeof value === 'object' && !Array.isArray(value)) {
				// Flatten nested objects
				sanitizedValue = JSON.stringify(value).substring(0, 100)
			}

			acc[sanitizedKey] = sanitizedValue
			return acc
		}, {} as Record<string, any>)
	}

	// Send event to GA4
	const sendEvent = (eventName: string, parameters?: Record<string, any>) => {
		const { gtag } = useGtag()
		const runtimeConfig = useRuntimeConfig()

		if (!gtag || !runtimeConfig.public.gtag.enabled) {
			log.info('GA4 not available or disabled')
			return
		}

		const sanitizedParams = sanitizeParameters(parameters)

		// Add to event history for debug panel
		eventHistory.value.unshift({
			name: eventName,
			params: sanitizedParams,
			timestamp: Date.now()
		})

		// Send to GA4
		gtag('event', eventName, sanitizedParams)
		log.info(`Event sent: ${eventName}`, sanitizedParams)
	}

	// Track event (queue if not initialized)
	const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
		// Validate event name
		if (!eventName || typeof eventName !== 'string') {
			log.error('Invalid event name:', eventName)
			return
		}

		// GA4 event name limits
		if (eventName.length > 40) {
			log.warn(`Event name too long (${eventName.length} chars), truncating:`, eventName)
			eventName = eventName.substring(0, 40)
		}

		// If initialized, send immediately
		if (isInitialized.value) {
			sendEvent(eventName, parameters)
			return
		}

		// Otherwise queue the event
		cleanQueue()

		// Check queue size
		if (queue.value.length >= config.value.maxQueueSize) {
			log.warn('Queue full, dropping oldest event')
			queue.value.shift()
		}

		// Add to queue
		queue.value.push({
			eventName,
			parameters,
			timestamp: Date.now()
		})

		log.info(`Event queued: ${eventName} (queue size: ${queue.value.length})`)
	}

	// Flush all queued events
	const flush = async () => {
		if (!isInitialized.value) {
			log.warn('Cannot flush queue before initialization')
			return
		}

		if (queue.value.length === 0) return

		const eventsToSend = [ ...queue.value ]
		queue.value = []

		log.info(`Flushing ${eventsToSend.length} queued events`)

		// Send all queued events
		eventsToSend.forEach(({
			eventName, parameters, timestamp
		}) => {
			const enrichedParams = {
				...parameters,
				queued_event: true,
				queue_delay_ms: Date.now() - timestamp
			}
			sendEvent(eventName, enrichedParams)
		})
	}

	// Initialize GA4
	const initialize = async (): Promise<void> => {
		if (isInitialized.value) return
		if (initPromise.value) return initPromise.value

		const runtimeConfig = useRuntimeConfig()
		if (!runtimeConfig.public.gtag.enabled) {
			log.info('GA4 is disabled via configuration')
			isInitialized.value = true // Set as initialized to prevent queuing
			return
		}

		initPromise.value = performInitialization()
		return initPromise.value
	}

	// Perform the actual initialization
	const performInitialization = async (): Promise<void> => {
		const {
			initialize: initGtag, gtag
		} = useGtag()

		try {
			const startTime = performance.now()

			// Initialize gtag with timeout
			await Promise.race([
				initGtag(),
				new Promise<never>((_, reject) =>
					setTimeout(() => reject(new Error('GA4 initialization timeout')), config.value.initTimeout)
				)
			])

			isInitialized.value = true
			const initTime = performance.now() - startTime

			log.info(`GA4 initialized in ${Math.round(initTime)}ms`)

			// Track initialization performance
			if (initTime > 1000) {
				gtag('event', 'timing_complete', {
					name: 'ga4_init_slow',
					value: Math.round(initTime),
					event_category: 'performance'
				})
			}

			// Flush any queued events
			await flush()
		} catch (error) {
			log.error('GA4 initialization failed:', error)
			// Don't throw - allow app to continue without GA4
			isInitialized.value = true // Prevent further queue buildup
		}
	}

	// Get current status
	const getStatus = () => ({
		isInitialized: isInitialized.value,
		queueLength: queue.value.length,
		oldestEventAge: queue.value.length > 0
			? Date.now() - (queue.value[0]?.timestamp || 0)
			: 0,
		config: config.value
	})

	// Update configuration
	const updateConfig = (newConfig: Partial<GA4Config>) => {
		config.value = { ...config.value,
			...newConfig }
		log.info('GA4 config updated:', newConfig)
	}

	// Get event history for debug panel
	const getEventHistory = () => eventHistory.value

	// Clear event history
	const clearEventHistory = () => {
		eventHistory.value = []
	}

	return {
		// State
		isInitialized: readonly(isInitialized),
		queueLength: computed(() => queue.value.length),

		// Methods
		trackEvent,
		initialize,
		flush,
		getStatus,
		updateConfig,

		// Debug methods
		getEventHistory,
		clearEventHistory
	}
}
